import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import Header from "../../component/Header";
import { Oval } from "react-loader-spinner";
import Sidebar from "../../component/Sidebar";
import FinanceTable from "../Summary/component/FinanceTable";
import FinanceSummary from "./component/FinanceSummary";
import {
  FiPlus, FiEdit2, FiTrash2, FiCalendar, FiDollarSign,
  FiTrendingUp, FiFilter, FiX, FiCheck, FiRefreshCw
} from "react-icons/fi";

const apiUrl = process.env.REACT_APP_API_URL;

function Finance() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);

  // Form State
  const [birr, setBirr] = useState("");
  const [yuan, setYuan] = useState("");
  const [rate, setRate] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  // Filter State
  const [filterMonth, setFilterMonth] = useState((new Date().getMonth() + 1).toString());
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());


  // Edit State
  const [isEditing, setIsEditing] = useState(null);
  const [editData, setEditData] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/finance`);
      setData(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrors(error.response?.data?.message || "Failed to fetch data");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const itemDate = new Date(item.date);
      const itemMonth = itemDate.getMonth() + 1;
      const itemYear = itemDate.getFullYear();

      const monthMatch = !filterMonth || itemMonth === parseInt(filterMonth);
      const yearMatch = !filterYear || itemYear === parseInt(filterYear);

      return monthMatch && yearMatch;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [data, filterMonth, filterYear]);


  const stats = useMemo(() => {
    const totalBirr = filteredData.reduce((acc, item) => acc + (parseFloat(item.birr) || 0), 0);
    const totalYuan = filteredData.reduce((acc, item) => acc + (parseFloat(item.yuan) || 0), 0);
    const avgRate = filteredData.length > 0
      ? (filteredData.reduce((acc, item) => acc + (parseFloat(item.rate) || 0), 0) / filteredData.length).toFixed(2)
      : 0;
    return { totalBirr, totalYuan, avgRate };
  }, [filteredData]);

  useEffect(() => {
    if (yuan && rate) {
      setBirr((parseFloat(yuan) * parseFloat(rate)).toFixed(2));
    }
  }, [yuan, rate]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!yuan || !rate || !date) return;
    try {
      setLoading(true);
      const items = { birr, yuan, rate, date };
      await axios.post(`${apiUrl}/postFinance`, items);
      setLoading(false);
      fetchData();
      setYuan("");
      setRate("");
      setBirr("");
    } catch (error) {
      setLoading(false);
      setErrors(error.response?.data?.message || "Failed to add record");
    }
  };

  const handleEditClick = (item) => {
    setIsEditing(item.ID);
    setEditData({
      birr: item.birr,
      yuan: item.yuan,
      rate: item.rate,
      date: item.date ? new Date(item.date).toISOString().split('T')[0] : ""
    });
  };

  const handleSave = async (id) => {
    try {
      setLoading(true);
      await axios.put(`${apiUrl}/updateFinance/${id}`, editData);
      setIsEditing(null);
      fetchData();
    } catch (error) {
      setLoading(false);
      setErrors(error.response?.data?.message || "Failed to update record");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      setLoading(true);
      await axios.delete(`${apiUrl}/deleteFinance/${id}`);
      fetchData();
    } catch (error) {
      setLoading(false);
      setErrors(error.response?.data?.message || "Failed to delete record");
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const months = [
    { v: "1", n: "January" }, { v: "2", n: "February" }, { v: "3", n: "March" },
    { v: "4", n: "April" }, { v: "5", n: "May" }, { v: "6", n: "June" },
    { v: "7", n: "July" }, { v: "8", n: "August" }, { v: "9", n: "September" },
    { v: "10", n: "October" }, { v: "11", n: "November" }, { v: "12", n: "December" }
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 pb-20">
      <Header />
      <Sidebar />

      <main className="pt-24 px-4 md:px-8 max-w-7xl mx-auto space-y-8">
        <FinanceSummary filterMonth={filterMonth} filterYear={filterYear} />

        {/* Hero Section / Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center space-x-4 hover:shadow-md transition-shadow">
            <div className="p-3 bg-green-50 rounded-xl text-green-600">
              <FiDollarSign className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Total Birr ({months.find(m => m.v === filterMonth)?.n || "All"} {filterYear})</p>
              <h3 className="text-2xl font-bold">{stats.totalBirr.toLocaleString()} <span className="text-sm font-normal text-slate-400">ETB</span></h3>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center space-x-4 hover:shadow-md transition-shadow">
            <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
              <FiRefreshCw className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Total Yuan</p>
              <h3 className="text-2xl font-bold">{stats.totalYuan.toLocaleString()} <span className="text-sm font-normal text-slate-400">¥</span></h3>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center space-x-4 hover:shadow-md transition-shadow">
            <div className="p-3 bg-purple-50 rounded-xl text-purple-600">
              <FiTrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Avg. Exchange Rate</p>
              <h3 className="text-2xl font-bold">{stats.avgRate}</h3>
            </div>
          </div>
        </div>

        {/* Action Bar: Add & Filter */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Add Form */}
          <div className="lg:col-span-2 bg-white p-8 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100">
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-2 h-8 bg-green-500 rounded-full"></div>
              <h2 className="text-xl font-bold text-slate-800">Add New Transaction</h2>
            </div>

            <form onSubmit={handlePost} className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Yuan (¥)</label>
                <input
                  className="w-full bg-slate-50 border-0 ring-1 ring-slate-200 focus:ring-2 focus:ring-green-500 rounded-xl p-3 outline-none transition-all"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={yuan}
                  onChange={(e) => setYuan(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Rate</label>
                <input
                  className="w-full bg-slate-50 border-0 ring-1 ring-slate-200 focus:ring-2 focus:ring-green-500 rounded-xl p-3 outline-none transition-all"
                  type="number"
                  step="0.01"
                  placeholder="20.5"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Birr (ETB)</label>
                <input
                  className="w-full bg-slate-100 border-0 ring-1 ring-slate-200 rounded-xl p-3 outline-none text-slate-500 cursor-not-allowed"
                  type="text"
                  value={birr}
                  readOnly
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Date</label>
                <input
                  className="w-full bg-slate-50 border-0 ring-1 ring-slate-200 focus:ring-2 focus:ring-green-500 rounded-xl p-3 outline-none transition-all"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>

              <div className="md:col-span-4 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-4 px-6 rounded-2xl flex items-center justify-center space-x-2 transition-all active:scale-95 disabled:opacity-50"
                >
                  {loading ? <Oval height={20} width={20} color="#fff" strokeWidth={3} /> : (
                    <>
                      <FiPlus className="w-5 h-5" />
                      <span>Record Transaction</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Filter Card */}
          <div className="bg-slate-900 p-8 rounded-3xl shadow-xl text-white">
            <div className="flex items-center space-x-2 mb-6">
              <FiFilter className="text-green-400 w-5 h-5" />
              <h2 className="text-xl font-bold">Quick Filter</h2>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Statement Year</label>
                <input
                  type="number"
                  className="w-full bg-slate-800 border-0 ring-1 ring-slate-700 focus:ring-2 focus:ring-green-500 rounded-xl p-3 outline-none transition-all text-white"
                  value={filterYear}
                  onChange={(e) => setFilterYear(e.target.value)}
                  placeholder="e.g. 2025"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Statement Month</label>
                <select
                  className="w-full bg-slate-800 border-0 ring-1 ring-slate-700 focus:ring-2 focus:ring-green-500 rounded-xl p-3 outline-none transition-all text-white"
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(e.target.value)}
                >
                  <option value="">All Transactions</option>
                  {months.map(m => <option key={m.v} value={m.v}>{m.n}</option>)}
                </select>
              </div>

              <button
                onClick={() => {
                  setFilterMonth((new Date().getMonth() + 1).toString());
                  setFilterYear(new Date().getFullYear().toString());
                }}
                className="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl transition-all"
              >
                Reset to Current
              </button>
            </div>

          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
          <div className="p-8 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">Transaction History</h2>
              <p className="text-slate-500 text-sm mt-1">Detailed log of all currency conversions</p>
            </div>
            {errors && (
              <div className="bg-red-50 text-red-600 px-4 py-2 rounded-xl flex items-center space-x-2 animate-pulse">
                <FiX className="w-4 h-4" />
                <span className="text-sm font-medium">{errors}</span>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="px-8 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">#</th>
                  <th className="px-8 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Date</th>
                  <th className="px-8 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Yuan (¥)</th>
                  <th className="px-8 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Rate</th>
                  <th className="px-8 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Birr (ETB)</th>
                  <th className="px-8 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-8 py-20 text-center text-slate-400 italic">
                      No transactions found for the selected period
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, index) => (
                    <tr key={item.ID} className="group hover:bg-slate-50/50 transition-colors">
                      {isEditing === item.ID ? (
                        <>
                          <td className="px-8 py-4 text-sm font-bold text-slate-900">{index + 1}</td>
                          <td className="px-8 py-4">
                            <input
                              type="date"
                              className="bg-white border ring-1 ring-slate-200 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-green-500"
                              value={editData.date}
                              onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                            />
                          </td>
                          <td className="px-8 py-4">
                            <input
                              type="number"
                              className="w-24 bg-white border ring-1 ring-slate-200 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-green-500"
                              value={editData.yuan}
                              onChange={(e) => {
                                const newYuan = e.target.value;
                                setEditData({ ...editData, yuan: newYuan, birr: (parseFloat(newYuan) * parseFloat(editData.rate || 0)).toFixed(2) });
                              }}
                            />
                          </td>
                          <td className="px-8 py-4">
                            <input
                              type="number"
                              className="w-24 bg-white border ring-1 ring-slate-200 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-green-500"
                              value={editData.rate}
                              onChange={(e) => {
                                const newRate = e.target.value;
                                setEditData({ ...editData, rate: newRate, birr: (parseFloat(editData.yuan || 0) * parseFloat(newRate)).toFixed(2) });
                              }}
                            />
                          </td>
                          <td className="px-8 py-4 font-bold text-slate-900">
                            {editData.birr}
                          </td>
                          <td className="px-8 py-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleSave(item.ID)}
                                className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all shadow-lg shadow-green-200"
                              >
                                <FiCheck className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setIsEditing(null)}
                                className="p-2 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300 transition-all"
                              >
                                <FiX className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-8 py-4 text-sm font-bold text-slate-300 group-hover:text-slate-900 transition-colors">
                            {index + 1}
                          </td>
                          <td className="px-8 py-4 text-sm text-slate-600 flex items-center space-x-2">
                            <FiCalendar className="text-slate-400" />
                            <span>{formatDate(item.date)}</span>
                          </td>
                          <td className="px-8 py-4 text-sm font-medium text-slate-900">
                            ¥{parseFloat(item.yuan).toLocaleString()}
                          </td>
                          <td className="px-8 py-4 text-sm text-slate-600">
                            {item.rate}
                          </td>
                          <td className="px-8 py-4 text-sm font-bold text-green-600">
                            {parseFloat(item.birr).toLocaleString()} ETB
                          </td>
                          <td className="px-8 py-4 text-right opacity-0 group-hover:opacity-100 transition-all">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleEditClick(item)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                title="Edit"
                              >
                                <FiEdit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(item.ID)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                title="Delete"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-slate-50 font-bold border-t border-slate-200">
                <tr>
                  <td className="px-8 py-6 text-slate-800" colSpan="2">TOTAL</td>
                  <td className="px-8 py-6 text-slate-800">¥{stats.totalYuan.toLocaleString()}</td>
                  <td className="px-8 py-6 text-slate-400 text-sm">AVG: {stats.avgRate}</td>
                  <td className="px-8 py-6 text-green-700">{stats.totalBirr.toLocaleString()} ETB</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* <div className="pt-10">
          <FinanceTable />
        </div> */}
      </main>
    </div>
  );
}

export default Finance;

